/**
 * Obieg dokumentu do portalu pracownika: kto go podpisuje i od kiedy pracownik
 * go widzi (#13, #72 z testu 2026-09-26).
 *
 * Cykl z ADR-0016 §3 to szkic → do akceptacji → do podpisu → podpisany.
 * Portal nie filtrował go wcale: pracownik widział i pobierał szkic świadectwa
 * pracy, a oświadczenie pracodawcy o zajęciu wynagrodzenia (art. 882 § 1
 * k.p.c.) czekało u dłużnika jako „Do podpisu", choć podpisuje je pracodawca
 * (ADR-0036 §4). Jedna reguła, z której czytają API i ekran:
 *
 *  - `obie_strony` — pracownik podpisuje dokument (umowa, aneks, protokół
 *    instruktażu BHP), więc widzi go już „do podpisu";
 *  - `pracodawca` — dokument wydaje albo doręcza pracodawca (świadectwo pracy
 *    z art. 97 § 1 k.p., zawiadomienie o karze z art. 110 k.p., informacje
 *    roczne), więc pracownik dostaje go dopiero wydany;
 *  - `brak` — dokument bez podpisu (regulamin, orzeczenie lekarskie).
 *
 * Szkic i dokument czekający na akceptację są dokumentami roboczymi kadr —
 * pracownik nie widzi ich nigdy. Archiwum pokazuje tylko to, co było wydane.
 */
import type { ContractType, DocumentStatus, DocumentType } from './enums'
import { DOCUMENT_TYPES, isEmploymentContract } from './enums'

export type DocumentSignatory = 'obie_strony' | 'pracodawca' | 'brak'

export const DOCUMENT_SIGNATORY: Record<DocumentType, DocumentSignatory> = {
  umowa: 'obie_strony',
  aneks: 'obie_strony',
  protokol_bhp: 'obie_strony',
  zaswiadczenie: 'pracodawca',
  swiadectwo: 'pracodawca',
  zawiadomienie_o_karze: 'pracodawca',
  ewidencja_czasu_pracy: 'pracodawca',
  oswiadczenie_zajecie: 'pracodawca',
  zgloszenie_rud: 'pracodawca',
  pit11: 'pracodawca',
  ift1r: 'pracodawca',
  ift1: 'pracodawca',
  rmua: 'pracodawca',
  inny: 'pracodawca',
  regulamin: 'brak',
  /* Odpisy akceptacji z procesu zatrudnienia — potwierdzenie zapoznania, nie podpis (grupa 134). */
  klauzula_rodo: 'brak',
  informacja_o_warunkach: 'brak',
  orzeczenie: 'brak',
}

/** Czy podpis pracownika jest częścią obiegu tego dokumentu. */
export function employeeSigns(type: DocumentType): boolean {
  return DOCUMENT_SIGNATORY[type] === 'obie_strony'
}

/** Rodzaje, które pracownik podpisuje — tylko one liczą się jako „do podpisu" w portalu. */
export const EMPLOYEE_SIGNED_DOCUMENT_TYPES: readonly DocumentType[] = DOCUMENT_TYPES.filter(employeeSigns)

export interface EmployeeDocumentFacts {
  readonly type: DocumentType
  readonly status: DocumentStatus
  readonly signedAt: Date | string | null
}

/** Czy pracownik widzi dokument w portalu i może go pobrać. */
export function isVisibleToEmployee(document: EmployeeDocumentFacts): boolean {
  switch (document.status) {
    case 'podpisany':
      return true
    case 'do_podpisu':
      return employeeSigns(document.type)
    case 'zarchiwizowany':
      /* Archiwizacja jest dozwolona z każdego stanu — widać tylko to, co wydano. */
      return document.signedAt !== null
    case 'szkic':
    case 'do_akceptacji':
      return false
  }
}

/**
 * Szablony kreatora opisują STOSUNEK PRACY (Fala 5, grupa 14): umowa
 * z art. 22 i 29 § 1 k.p., aneks z art. 29 § 4, zaświadczenie o zatrudnieniu
 * i świadectwo pracy z art. 97 § 1 (należy się tylko przy ustaniu stosunku
 * pracy). Umowy zlecenia (art. 734 k.c.), o dzieło (art. 627 k.c.) ani B2B
 * nie wolno opisywać cechami stosunku pracy — dla nich szablonu nie ma (D95).
 */
export const TEMPLATE_CONTRACT_SCOPE = {
  umowa: 'employment',
  aneks: 'employment',
  zaswiadczenie: 'employment',
  swiadectwo: 'employment',
} as const satisfies Partial<Record<DocumentType, 'employment'>>

/** Czy szablon wolno wystawić osobie z tym rodzajem umowy (brak rodzaju — nie). */
export function templateAllowedFor(type: DocumentType, contractType: ContractType | null): boolean {
  if (!(type in TEMPLATE_CONTRACT_SCOPE) || contractType === null) return false
  return isEmploymentContract(contractType)
}
